import React from "react";
import Highcharts, { dateFormat } from "highcharts";
import { DateTime } from "luxon";

import { Box } from "@material-ui/core";

import StockLine from "components/visuals/StockLine";
import useColourPalette from "components/visuals/useColourPalette";

import { useAppDispatch, useAppSelector } from "store";
import {
    selectFootfallOverTimeGranularity,
    selectFootfallOverTimeMeasure,
    selectHasErrors,
    selectIsLoading,
    selectStoreFootfallOverTime,
    setFootfallOverTimeGranularity
} from "modules/customer/insights/portfolioNew/footfallNew/footfallNewSlice";

import numberFormatter from "utils/numberFormatter";
import { selectComparator, selectStore } from "modules/customer/insights/portfolioNew/portfolioSlice";
import { FootfallMeasureType } from "modules/customer/insights/portfolioNew/footfallNew/footfallMeasureType";
import { FootfallGranularity } from "modules/customer/insights/portfolioNew/footfallNew/footfallGranularity";
import stringUtils from "utils/stringUtils";

const FootfallOverTime: React.FC = () => {
    const dispatch = useAppDispatch();
    const colourPalette = useColourPalette();
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const selectedStore = useAppSelector(selectStore);
    const comparator = useAppSelector(selectComparator);
    const footfallMeasure = useAppSelector(selectFootfallOverTimeMeasure); // ToDo: Add toggle button to control this
    const footfallOverTime = useAppSelector(selectStoreFootfallOverTime);
    const chartGranularity = useAppSelector(selectFootfallOverTimeGranularity);

    function generateGroupingKey(timestamp: number, granularity: FootfallGranularity) {
        if (granularity === FootfallGranularity.Quarter) {
            const month = DateTime.fromMillis(timestamp).month;
            const quarter = Math.ceil(month / 3);
            return `Q${quarter} ` + dateFormat("%Y", timestamp);
        }
        else if (granularity === FootfallGranularity.Year) {
            return dateFormat("%Y", timestamp);
        }
        else {
            return dateFormat("%B %Y", timestamp);
        }
    }

    function getCustomData(pointName: string, key: string, chartSeriesData: Highcharts.SeriesOptionsType[]) {
        const seriesData = chartSeriesData.find(item => item.name === pointName);
        // @ts-ignore
        return seriesData?.data.filter(item => item.custom.key === key);
    }

    const series: Highcharts.SeriesOptionsType[] = [];
    const aggregationType = footfallMeasure === FootfallMeasureType.NumberOfVisits ? "sum" : "average";
    const selectedButton = chartGranularity === FootfallGranularity.Month
        ? 0
        : chartGranularity === FootfallGranularity.Quarter ? 1 : 2;

    series.push({
        type: "spline",
        showInNavigator: true,
        name: selectedStore?.name,
        data: footfallOverTime.selectedStore.map(item => ({
            x: item.date.toMillis(),
            y: item[footfallMeasure],
            custom: {
                key: generateGroupingKey(item.date.toMillis(), chartGranularity),
                value: footfallMeasure === FootfallMeasureType.NumberOfVisits ?
                    item[FootfallMeasureType.DwellTime] :
                    item[FootfallMeasureType.NumberOfVisits]
            }
        })),
        color: colourPalette.comparators[0]
    });

    series.push({
        type: "spline",
        showInNavigator: true,
        name: comparator?.name,
        data: footfallOverTime.comparator.map(item => ({
            x: item.date.toMillis(),
            y: item[footfallMeasure],
            custom: {
                key: generateGroupingKey(item.date.toMillis(), chartGranularity),
                value: footfallMeasure === FootfallMeasureType.NumberOfVisits ?
                    item[FootfallMeasureType.DwellTime] :
                    item[FootfallMeasureType.NumberOfVisits]
            }
        })),
        color: colourPalette.comparators[1]
    });

    const options: Highcharts.Options = {
        xAxis: {
            type: 'datetime'
        },
        yAxis: {
            title: {
                text: (footfallMeasure === FootfallMeasureType.NumberOfVisits ? "Visits" : "Dwell time (minutes)")
            },
            labels: {
                formatter: function () {
                    return numberFormatter.toDecimalPlaces(this.value, 1);
                }
            }
        },
        legend: {
            enabled: true
        },
        series,
        navigator: {
            series: {
                fillColor: 'none',
                fillOpacity: 0
            }
        },
        rangeSelector: {
            allButtonsEnabled: true,
            selected: selectedButton,
            buttons: [{
                type: "month",
                count: 12,
                text: "Month",
                dataGrouping: {
                    enabled: true,
                    forced: true,
                    units: [["month", [1]]],
                    approximation: aggregationType
                },
                events: {
                    click: function () {
                        dispatch(setFootfallOverTimeGranularity(FootfallGranularity.Month));
                    }
                },
                preserveDataGrouping: true
            }, {
                type: "month",
                count: 24,
                text: "Quarter",
                dataGrouping: {
                    enabled: true,
                    forced: true,
                    units: [["month", [3]]],
                    approximation: aggregationType
                },
                events: {
                    click: function () {
                        dispatch(setFootfallOverTimeGranularity(FootfallGranularity.Quarter));
                    }
                },
                preserveDataGrouping: true
            }, {
                type: "year",
                count: 3,
                text: "Year",
                dataGrouping: {
                    enabled: true,
                    forced: true,
                    units: [["year", [1]]],
                    approximation: aggregationType
                },
                events: {
                    click: function () {
                        dispatch(setFootfallOverTimeGranularity(FootfallGranularity.Year));
                    }
                },
                preserveDataGrouping: true
            }]
        },
        tooltip: {
            enabled: true,
            shared: true,
            split: false,
            headerFormat: ``,
            footerFormat: ``,
            formatter: function () {
                let header = "";
                const timestamp = this.x as number;

                if (chartGranularity === FootfallGranularity.Year) {
                    header = dateFormat("%Y", timestamp);
                }
                else if (chartGranularity === FootfallGranularity.Month) {
                    header = dateFormat("%B %Y", timestamp);
                }
                else {
                    const month = DateTime.fromMillis(timestamp).month;
                    const quarter = Math.ceil(month / 3);
                    header = `Q${quarter} ` + dateFormat("%Y", timestamp);
                }

                const categoriesArr: string[] = [];
                const categoriesFormatArr: string[] = [];
                const valuesArr: string[] = [];

                this.points?.forEach(item => {
                    footfallMeasure === FootfallMeasureType.NumberOfVisits
                        ? categoriesArr.push(item.point.series.name === selectedStore?.name ? item.point.series.name + " (avg. per call)" : item.point.series.name,
                            "Visits", "Dwell time"
                        )
                        : categoriesArr.push(item.point.series.name === selectedStore?.name ? item.point.series.name + " (avg. per call)" : item.point.series.name,
                            "Dwell time", "Visits"
                        );
                    categoriesFormatArr.push(`color:${item.point?.series?.color};font-weight:bold`, "", "");

                    const pointKey = generateGroupingKey(item.point.x, chartGranularity);
                    const customData = getCustomData(item.point.series.name, pointKey, series);
                    //@ts-ignore
                    const numberOfVisits = numberFormatter.toDecimalPlaces(footfallMeasure === FootfallMeasureType.NumberOfVisits ? item.point.y : customData.reduce((sum, item) => sum + item.custom.value, 0), 1);
                    //@ts-ignore
                    const dwellTimeValue = numberFormatter.toDecimalPlaces(footfallMeasure === FootfallMeasureType.DwellTime ? item.point.y : (customData.reduce((sum, item) => sum + item.custom.value, 0) / customData.length), 1);
                    const dwellTime = `${dwellTimeValue} minutes`;
                    footfallMeasure === FootfallMeasureType.NumberOfVisits
                        ? valuesArr.push("", numberOfVisits, dwellTime)
                        : valuesArr.push("", dwellTime, numberOfVisits);
                });
                return `<table>${stringUtils.tooltipHTML(
                    categoriesArr,
                    {
                        header: header,
                        values: valuesArr,
                        categoryFormattingArr: categoriesFormatArr
                    }
                )}</table>`;
            },
            useHTML: true
        }
    };

    return (
        <Box data-cy="total-cost-trends-over-time">
            <StockLine loading={isLoading} error={hasErrors} options={options} globalOptions={{}} />
        </Box>
    );
};

export default FootfallOverTime;
